import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSyncAlt, FaTimes, FaArrowRight } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Banner que se muestra en el Dashboard de un usuario Fit Evolution cuando
 * corresponde completar el formulario de actualización (cada 4 semanas o
 * 4 días antes de vencer si es plan mensual).
 */
export default function FitEvolutionUpdateBanner({ user }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.email) return;
    if (user?.subscription_plan !== 'basic') return;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/personalized-plans/fit-evolution/update-check/${encodeURIComponent(user.email)}`
        );
        setStatus(res.data);
      } catch (e) {
        // silent fail
      }
    })();
    // Respetar dismiss del día actual
    const today = new Date().toISOString().slice(0, 10);
    if (sessionStorage.getItem('fit_evo_update_dismissed') === today) {
      setDismissed(true);
    }
  }, [user]);

  if (!status?.has_pending_update || dismissed) return null;

  const dismiss = () => {
    const today = new Date().toISOString().slice(0, 10);
    sessionStorage.setItem('fit_evo_update_dismissed', today);
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="rounded-2xl p-4 md:p-5 mb-4 relative"
        style={{
          background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow),0.16), rgba(0,240,255,0.06))',
          border: '1.5px solid rgba(var(--brand-primary-glow),0.4)'
        }}
        data-testid="fit-evo-update-banner"
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-white/60 hover:text-white p-1"
          aria-label="Descartar por hoy"
          data-testid="dismiss-update-banner"
        >
          <FaTimes />
        </button>
        <div className="flex items-start gap-4 pr-6">
          <div className="text-3xl md:text-4xl leading-none pt-1">🔄</div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-pink-300/90 font-bold mb-1">
              Fit Evolution · Actualización disponible
            </p>
            <h3 className="text-base md:text-lg font-black text-white mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Es momento de ajustar tu plan
            </h3>
            <p className="text-xs md:text-sm text-white/85 mb-3 leading-snug">
              {status.message ||
                'Cuéntanos tu peso, medidas y evolución para recalcular tu plan a tu progreso real.'}
            </p>
            <button
              onClick={() => navigate('/fit-evolution/update')}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white hover:scale-[1.02] transition-all"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
              data-testid="open-update-form"
            >
              <FaSyncAlt className="text-xs" /> Actualizar mi plan <FaArrowRight className="text-xs" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
